import {
  Badge,
  Button,
  Card,
  Code,
  Drawer,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { api, unwrap } from '../api.ts';
import { Empty, Failure, Loading, Page, useSave } from '../ui.tsx';

const KINDS = ['payments', 'parking', 'access', 'calendar', 'email', 'push'] as const;
type Kind = (typeof KINDS)[number];

const PROVIDERS: Record<Kind, string[]> = {
  payments: ['stripe', 'fake'],
  parking: ['parkingaround', 'fake'],
  access: ['salto', 'hid', 'fake'],
  calendar: ['microsoft_graph', 'fake'],
  email: ['postmark', 'fake'],
  push: ['expo', 'fake'],
};

const KIND_LABEL: Record<Kind, string> = {
  payments: 'Payments',
  parking: 'Parking',
  access: 'Door access',
  calendar: 'Calendar sync',
  email: 'Email',
  push: 'Push notifications',
};

interface Setting {
  kind: Kind;
  provider: string;
  buildingId: string | null;
  settings: Record<string, unknown>;
  secretRef: string | null;
}

function SettingForm(props: { kind: Kind; setting: Setting | null; onDone: () => void }) {
  const [form, setForm] = useState({
    provider: props.setting?.provider ?? PROVIDERS[props.kind][0] ?? 'fake',
    secretRef: props.setting?.secretRef ?? '',
    settings: JSON.stringify(props.setting?.settings ?? {}, null, 2),
  });
  const [invalid, setInvalid] = useState<string | null>(null);
  const save = useSave({
    run: (settings: Record<string, unknown>) =>
      unwrap(
        api().PUT('/v1/integration-settings', {
          body: {
            kind: props.kind,
            provider: form.provider,
            buildingId: props.setting?.buildingId ?? null,
            settings,
            secretRef: form.secretRef || null,
          },
        }),
      ),
    invalidates: [['integrations'], ['audit']],
    success: `${KIND_LABEL[props.kind]} saved`,
    onDone: props.onDone,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    try {
      const parsed: unknown = JSON.parse(form.settings);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))
        throw new Error('not an object');
      setInvalid(null);
      save.mutate(parsed as Record<string, unknown>);
    } catch {
      setInvalid('Settings must be a JSON object');
    }
  };
  return (
    <form onSubmit={submit}>
      <Stack>
        <Select
          label="Provider"
          data={PROVIDERS[props.kind]}
          value={form.provider}
          onChange={(provider) => provider && setForm({ ...form, provider })}
          allowDeselect={false}
        />
        <TextInput
          label="Secret name"
          description="The Key Vault secret holding the API key. The key itself never passes through the console."
          placeholder={`${props.kind}-api-key`}
          value={form.secretRef}
          onChange={(event) => setForm({ ...form, secretRef: event.currentTarget.value })}
        />
        <Textarea
          label="Settings"
          description="Non-secret options as JSON"
          autosize
          minRows={4}
          ff="monospace"
          value={form.settings}
          error={invalid}
          onChange={(event) => setForm({ ...form, settings: event.currentTarget.value })}
        />
        <Failure error={save.error} />
        <Button type="submit" loading={save.isPending}>
          Save
        </Button>
      </Stack>
    </form>
  );
}

export function IntegrationsPage() {
  const settings = useQuery({
    queryKey: ['integrations'],
    queryFn: () => unwrap(api().GET('/v1/integration-settings')),
  });
  const [editing, setEditing] = useState<Kind | null>(null);
  const settingFor = (kind: Kind) =>
    (settings.data?.find((setting) => setting.kind === kind && setting.buildingId === null) as
      | Setting
      | undefined) ?? null;
  return (
    <Page
      title="Integrations"
      description="Which vendor each capability uses for your organization. Secrets stay in Key Vault; only their names are stored here."
    >
      <Card withBorder padding="lg">
        {settings.isPending ? <Loading /> : null}
        <Failure error={settings.error} />
        {settings.data ? (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Capability</Table.Th>
                <Table.Th>Provider</Table.Th>
                <Table.Th>Secret</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {KINDS.map((kind) => {
                const setting = settingFor(kind);
                return (
                  <Table.Tr key={kind}>
                    <Table.Td fw={600}>{KIND_LABEL[kind]}</Table.Td>
                    <Table.Td>
                      {setting ? (
                        <Badge variant="light" color={setting.provider === 'fake' ? 'yellow' : 'green'}>
                          {setting.provider === 'fake' ? 'simulated' : setting.provider}
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Not set up
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>{setting?.secretRef ? <Code>{setting.secretRef}</Code> : '—'}</Table.Td>
                    <Table.Td ta="right">
                      <Button size="xs" variant="default" onClick={() => setEditing(kind)}>
                        {setting ? 'Edit' : 'Set up'}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        ) : null}
        {settings.data?.some((setting) => setting.buildingId !== null) ? (
          <Group mt="md">
            <Empty>Building-level overrides exist and are kept as they are.</Empty>
          </Group>
        ) : null}
      </Card>
      <Drawer
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? KIND_LABEL[editing] : ''}
        position="right"
      >
        {editing ? (
          <SettingForm kind={editing} setting={settingFor(editing)} onDone={() => setEditing(null)} />
        ) : null}
      </Drawer>
    </Page>
  );
}
